import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../hooks/useSEO';

const PageContainer = styled.div`
  min-height: ${(p) => (p.$embedded ? 'auto' : '100vh')};
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: ${(p) => (p.$embedded ? '0' : '90px')};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 0;
  background: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-light) 100%);
  color: white;
  margin-bottom: 3rem;
  border-radius: 0 0 24px 24px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.95;
`;

const ContentSection = styled.section`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: var(--brand-blue);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  border-bottom: 3px solid var(--brand-red);
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.7rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CantonSection = styled.div`
  margin-bottom: 3.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const CantonTitle = styled.h3`
  color: var(--brand-blue);
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 3px solid var(--brand-red);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const ClubsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.25rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ClubCard = styled.div`
  display: block;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  color: inherit;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--brand-blue) 0%, var(--brand-red) 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.15);
    border-color: var(--brand-blue);
    
    &::before {
      transform: scaleX(1);
    }
    
    .club-name {
      color: var(--brand-blue);
    }
    
    .club-icon {
      transform: translateX(4px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ClubPrimaryLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

const ClubBookingLink = styled.a`
  display: inline-block;
  margin-top: 0.65rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--brand-red);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ClubName = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--brand-blue);
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ClubIcon = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  color: var(--brand-red);
  transition: transform 0.3s ease;
  font-size: 0.9rem;
`;

const OfficialPartnerSection = styled.div`
  margin-bottom: 4rem;
  padding-bottom: 3rem;
  border-bottom: 3px solid var(--brand-red);
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    padding-bottom: 2rem;
  }
`;

const OfficialPartnerTitle = styled.h3`
  color: var(--brand-blue);
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 3px solid var(--brand-red);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const OfficialPartnerCard = styled.a`
  display: block;
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  border: 3px solid var(--brand-red);
  border-radius: 16px;
  padding: 2rem;
  text-decoration: none;
  color: white;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(4, 54, 99, 0.2);
  max-width: 400px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.5) 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &::after {
    content: '⭐';
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.5rem;
    opacity: 0.8;
  }
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 24px -4px rgba(4, 54, 99, 0.4);
    border-color: #ffffff;
    
    &::before {
      transform: scaleX(1);
    }
    
    .partner-name {
      color: #ffffff;
      transform: translateX(4px);
    }
    
    .partner-icon {
      transform: translateX(6px);
    }
  }
  
  &:active {
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 1.75rem;
    border-radius: 14px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const PartnerName = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const PartnerIcon = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  color: #ffffff;
  transition: transform 0.3s ease;
  font-size: 1.1rem;
  font-weight: 600;
`;

const PartnerClubsPage = ({ embedded = false }) => {
  const { t } = useTranslation();
  useSEO(
    embedded
      ? {}
      : {
          title: t('partnerClubsPage.seo.title'),
          description: t('partnerClubsPage.seo.description'),
          keywords: t('partnerClubsPage.seo.keywords'),
          canonical: 'https://helveticpadelseries.ch/partner-clubs',
        }
  );

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Club data organized by canton (alphabetically). Padelwerk locations: venue page + optional booking (Playtomic / Arenakey).
  const clubsByCanton = [
    {
      canton: 'Aargau',
      clubs: [
        { name: 'Padel Heights', url: 'https://www.padelheights.ch/' },
        { name: 'PadelNext', url: 'https://padelnext.de/' },
        {
          name: 'Padelwerk Klingnau',
          url: 'https://playtomic.com/clubs/padelwerk-klingnau?q=PADE'
        },
        {
          name: 'Padelwerk Safenwil',
          url: 'https://padelwerk.ch/en/locations/padelwerk-safenwil/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-safenwil'
        },
        {
          name: 'Padelwerk Wallbach',
          url: 'https://padelwerk.ch/en/locations/padelwerk-wallbach/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-wallbach'
        }
      ]
    },
    {
      canton: 'Basel',
      clubs: [
        { name: 'Padel Frenkendorf', url: 'https://www.padelfrenkendorf.ch/' },
        { name: 'Padelwerk', url: 'https://padelwerk.ch/en/' },
        {
          name: 'Padelwerk Basel Messe',
          url: 'https://padelwerk.ch/en/locations/padelwerk-basel-messe/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-messe-basel?q=F'
        },
        {
          name: 'Padelwerk Basel Stücki Park',
          url: 'https://padelwerk.ch/en/locations/padelwerk-basel-stuecki-park/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-basel-stucki-area'
        },
        {
          name: 'Padelwerk Münchenstein',
          url: 'https://padelwerk.ch/en/locations/padelwerk-muenchenstein/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-munchenstein?q='
        },
        {
          name: 'Padelwerk Pratteln',
          url: 'https://padelwerk.ch/en/locations/padelwerk-pratteln/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-pratteln?q=PADEL'
        },
        { name: 'Play Padel Pratteln', url: 'https://www.playpadel.ch/index.php/en/' }
      ]
    },
    {
      canton: 'Bern',
      clubs: [
        { name: 'Centre Sportif Reconvilier (Spoort)', url: 'https://reconvilier.spoort.ch/fr/home' },
        { name: 'Padelcourt Giessenbad Belp', url: 'https://padelgiessenbadbelp.ch/' },
        { name: 'Padelino Lyss', url: 'https://www.padelino.ch/' },
        { name: 'Padelwerk Lyss', url: 'https://padelwerk.ch/en/locations/padelwerk-lyss/' },
        {
          name: 'Padelwerk Bern',
          url: 'https://padelwerk.ch/en/locations/padelwerk-bern/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-bern?q=PADEL-20'
        },
        { name: 'TC Wohlensee', url: 'https://www.tcwohlensee.ch/' },
        { name: 'TPC Biel - Bienne', url: 'https://www.tpcbiel-bienne.ch/' }
      ]
    },
    {
      canton: 'Fribourg',
      clubs: [
        { name: 'Casa De Padel Murten', url: 'https://www.cdpadel.ch/' },
        { name: 'Gruyère Padel Club', url: 'https://gruyerepadel.ch/' },
        {
          name: 'Onyria Padel Lab',
          url: 'https://playtomic.com/clubs/onyria-padel-lab',
        },
        { name: 'Padel Parc Fribourg', url: 'https://padelparc.ch/fribourg/' },
        { name: 'The Padel Switzerland', url: 'https://www.the-padel.ch/en' }
      ]
    },
    {
      canton: 'Geneva',
      clubs: [
        { name: 'Padel First', url: 'https://www.padelfirst.ch/' },
        { name: 'Padel Station', url: 'https://padelstation.ch/' },
        { name: 'TC International Chambésy', url: 'https://internationaltennis.club/' },
        { name: 'Tennis Club de Collonge-Bellerive', url: 'https://tccb.ch/padel-2/' }
      ]
    },
    {
      canton: 'Glarus',
      clubs: [
        {
          name: 'Padelwerk Netstal (Wiggispark)',
          url: 'https://padelwerk.ch/en/locations/padelwerk-netstal-wiggispark/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-netstal'
        }
      ]
    },
    {
      canton: 'Lucerne',
      clubs: [{ name: 'Padeldrome', url: 'https://www.padeldrome.ch/' }]
    },
    {
      canton: 'Neuchâtel',
      clubs: [
        { name: 'CIS Marin', url: 'https://www.cis-marin.ch/' },
        { name: 'Padel Club Colombier', url: 'https://padelclub.ch/?fbclid=PAZXh0bgNhZW0CMTEAAaa9Vg1buaj5uoI3lb5bINSJVU08hMDNmz3pKUrya8t65yh6hwwbWbC5Uek_aem_pLyJiUjxl_9EWIhk2vE3nA' }
      ]
    },
    {
      canton: 'Schaffhausen',
      clubs: [{ name: 'Padel Vibez', url: 'https://padelvibez.ch/' }]
    },
    {
      canton: 'Schwyz',
      clubs: [
        {
          name: 'Padelwerk Altendorf',
          url: 'https://padelwerk.ch/en/locations/padelwerk-altendorf/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-altendorf'
        }
      ]
    },
    {
      canton: 'Solothurn',
      clubs: [
        {
          name: 'Padelwerk Oensingen (single courts)',
          url: 'https://padelwerk.ch/en/locations/padelwerk-oensingen/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-oensingen?q=PAD'
        },
        {
          name: 'Padelwerk Oensingen 2 (doubles courts)',
          url: 'https://padelwerk.ch/en/locations/padelwerk-oensingen-2/',
          bookingUrl: 'https://arenakey.app/stations/padelwerk-oensingen-2'
        },
        {
          name: 'Padelwerk Subingen',
          url: 'https://padelwerk.ch/en/locations/padelwerk-subingen/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-subingen'
        }
      ]
    },
    {
      canton: 'St. Gallen',
      clubs: [
        { name: 'PointPadel', url: 'https://www.pointpadel.ch/' },
        {
          name: 'Padelwerk Abtwil, Säntispark',
          url: 'https://padelwerk.ch/standorte/padelwerk-abtwil-saentispark/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-abtwil'
        },
        {
          name: 'Padelwerk Altstätten',
          url: 'https://padelwerk.ch/en/locations/padelwerk-altstaetten/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-altstatten'
        },
        {
          name: 'Padelwerk Schwarzenbach',
          url: 'https://padelwerk.ch/en/locations/padelwerk-schwarzenbach/',
          bookingUrl: 'https://arenakey.app/stations/padelwerk-schwarzenbach'
        }
      ]
    },
    {
      canton: 'Thurgau',
      clubs: [
        {
          name: 'Padeleros Egnach',
          url: 'https://www.padeleros.ch/location-padeleros-egnach',
          bookingUrl: 'https://playtomic.com/clubs/padeleros-tc-egnach'
        },
        { name: 'Padel Tennis am See', url: 'https://padeltennisamsee.ch/' },
        {
          name: 'Padelwerk Arbon',
          url: 'https://padelwerk.ch/en/locations/padelwerk-arbon/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-arbon?q=PADEL-2'
        }
      ]
    },
    {
      canton: 'Ticino',
      clubs: [
        { name: 'B-Arena', url: 'https://b-arena.ch/' },
        { name: 'Grancia Padel Club', url: 'https://granciapadelclub.ch/' },
        { name: 'Padel Club Contone', url: 'https://padelclubcontone.ch/' },
        { name: 'Padel Move', url: 'https://padelmove.ch/' },
        { name: 'The Padel Lab.', url: 'https://thepadellabgroup.ch/' }
      ]
    },
    {
      canton: 'Valais',
      clubs: [
        { name: 'Padel de Riddes (SportUp)', url: 'https://padel-sportup.ch/' },
        { name: 'Rackets Academy - Salgesch', url: 'https://www.racketsacademy.ch/?srsltid=AfmBOoqdGAb34Aq1ZArW_KbFo5dF2dksUA50RNnxk_KEzUku5ue-s8yv' },
        { name: 'Rackets Academy Sion', url: 'https://www.racketsacademy.ch/' },
        { name: 'Tennis & Padel Club Collombey-Muraz', url: 'https://tccm.ch/' }
      ]
    },
    {
      canton: 'Vaud',
      clubs: [
        { name: 'Centre Fairplay - Puidoux', url: 'https://centrefairplay.ch/le-centre' },
        { name: 'Club de Tennis-Padel Aigle', url: 'https://tcaigle.ch/' },
        { name: 'Everness Hotel & Resort', url: 'https://everness.ch/fr/padel/' },
        {
          name: 'Green Club (Vaud)',
          url: 'https://green-club.ch/bienvenue',
          bookingUrl:
            'https://reservations.green-club.ch/tableau_padel.php?responsive=true&d=ZwNlAv0jAF0kZD=='
        },
        { name: 'Padel One Echandens', url: 'https://www.padel-one.ch/' },
        { name: 'Padel Parc Etoy', url: 'https://padelparc.ch/etoy/' },
        { name: 'Padel Parc Préverenges', url: 'https://padelparc.ch/preverenges/' },
        { name: 'Padel Parc Yverdon', url: 'https://padelparc.ch/yverdon/' },
        { name: 'Tennis Club Gland', url: 'https://tcgland.ch/autres-sports/#padel' },
        { name: 'Urban Padel Lausanne', url: 'https://urbanpadel.ch/' }
      ]
    },
    {
      canton: 'Zug',
      clubs: [{ name: 'Pm5', url: 'https://www.pm5.ch/' }]
    },
    {
      canton: 'Zurich',
      clubs: [
        { name: 'Padel Club Switzerland', url: 'https://padelclubswitzerland.ch/' },
        { name: 'Padelarena', url: 'https://www.padelarena.ch/' },
        { name: 'Padelino', url: 'https://www.padelino.ch/' },
        { name: 'Padeleros', url: 'https://www.padeleros.ch/' },
        {
          name: 'Padelwerk Bubikon',
          url: 'https://padelwerk.ch/en/locations/padelwerk-bubikon/',
          bookingUrl: 'https://playtomic.com/clubs/padelwerk-bubikon?q=PADE'
        },
        { name: 'PDL Zurich', url: 'https://pdlu.com/pdl-zurich/' },
        { name: 'UniPadel Zurich', url: 'https://www.instagram.com/unipadelzurich/' }
      ]
    }
  ];

  return (
    <PageContainer $embedded={embedded}>
      <HeroSection>
        <Container>
          <HeroTitle>{t('partnerClubsPage.hero.title')}</HeroTitle>
          <HeroSubtitle>{t('partnerClubsPage.hero.subtitle')}</HeroSubtitle>
        </Container>
      </HeroSection>

      <Container>
        <ContentSection>
          <SectionTitle>{t('partnerClubsPage.partnershipsTitle')}</SectionTitle>

          {/* Official Partner Section */}
          <OfficialPartnerSection>
            <OfficialPartnerTitle>{t('partnerClubsPage.officialPartnerTitle')}</OfficialPartnerTitle>
            <OfficialPartnerCard
              href="https://www.decathlon.ch/en?utm_source=google&utm_medium=cpc&utm_campaign=ch_t-perf_ct-search_n-brand-exact_ts-bra_f-cv_o-roas_spu-msp_pnl-ecom_l-en_pp-gads_bm-cpc_pr-cpc_at-kwd_arcane&utm_term=&utm_content=&gad_source=1&gad_campaignid=22464834654&gbraid=0AAAAABUWyCNaprtkPhOP4BpQ53zgb30xE&gclid=Cj0KCQiAvtzLBhCPARIsALwhxdpJfVFufCjFINr-ez7up95hYoQhKZ6KzYPQv-QqeOzMZ5R2Vt45RkIaAnh3EALw_wcB"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PartnerName className="partner-name">
                Decathlon Switzerland
                <PartnerIcon className="partner-icon">→</PartnerIcon>
              </PartnerName>
            </OfficialPartnerCard>
          </OfficialPartnerSection>
          
          {/* Clubs Partner Section */}
          <SectionTitle style={{ marginTop: '2rem' }}>{t('partnerClubsPage.clubsPartnerTitle')}</SectionTitle>
          {clubsByCanton.map((cantonData) => (
            <CantonSection key={cantonData.canton}>
              <CantonTitle>{cantonData.canton}</CantonTitle>
              <ClubsGrid>
                {cantonData.clubs.map((club, index) => {
                  const primary = club.url || club.bookingUrl || '#';
                  const showBooking =
                    club.bookingUrl &&
                    club.bookingUrl !== primary;
                  return (
                    <ClubCard key={index}>
                      <ClubPrimaryLink
                        href={primary}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (primary === '#') e.preventDefault();
                        }}
                      >
                        <ClubName className="club-name">
                          {club.name}
                          {primary !== '#' && <ClubIcon className="club-icon">→</ClubIcon>}
                        </ClubName>
                      </ClubPrimaryLink>
                      {showBooking && (
                        <ClubBookingLink
                          href={club.bookingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Book online →
                        </ClubBookingLink>
                      )}
                    </ClubCard>
                  );
                })}
              </ClubsGrid>
            </CantonSection>
          ))}
        </ContentSection>
      </Container>
    </PageContainer>
  );
};

export default PartnerClubsPage;
